import { resolutions } from '@/pages/AdvancedEditor/constants';
import { getAvatarElementFromPage } from '@/pages/AdvancedEditor/utils/getAvatarFromPage';
import { getBoundedAvatarSize } from '@/pages/AdvancedEditor/utils/getBoundedAvatarSize';
import { CropMode } from '@/utils/types';
import { PageType } from 'polotno/model/page-model';
import { StoreType } from 'polotno/model/store';
import { polotnoTypes } from '../../types';
import { checkPolotnoType } from '../../utils/checkPolotnoType';

interface Size {
    width: number;
    height: number;
}

interface Position {
    x: number;
    y: number;
}

interface RotatedDimensions extends Size {
    offsetX: number; // Distance to shift right to keep object visible
    offsetY: number; // Distance to shift down to keep object visible
}

/**
 * Calculates the dimensions and offsets of a rotated rectangle
 * @param width - Original width before rotation
 * @param height - Original height before rotation
 * @param angleDegrees - Rotation angle in degrees
 * @returns New dimensions and required offsets after rotation
 *
 * How it works:
 * 1. Convert angle to radians for Math.cos/sin
 * 2. Calculate all corner positions after rotation
 * 3. Find rotated width/height using the trigonometric formula
 * 4. Calculate minimum offsets needed to keep all corners visible
 */
export function calculateRotatedDimensions(width: number, height: number, angleDegrees: number): RotatedDimensions {
    // Convert to radians and get sin/cos once for efficiency
    const angle = (angleDegrees * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Calculate rotated width and height using absolute values to handle all quadrants
    const newWidth = Math.abs(width * cos) + Math.abs(height * sin);
    const newHeight = Math.abs(height * cos) + Math.abs(width * sin);

    // Four corners after rotation relative to top-left (0,0):
    // - Top-left stays at origin
    // - Top-right rotates width distance
    // - Bottom-left rotates height distance
    // - Bottom-right combines both rotations
    const corners = [
        { x: 0, y: 0 },
        { x: width * cos, y: width * sin },
        { x: -height * sin, y: height * cos },
        { x: width * cos - height * sin, y: width * sin + height * cos },
    ];

    // Get required offsets by negating most negative coordinates
    // This effectively shifts everything back into positive space
    return {
        width: newWidth,
        height: newHeight,
        offsetX: -Math.min(0, ...corners.map(c => c.x)),
        offsetY: -Math.min(0, ...corners.map(c => c.y)),
    };
}

/** Calculate new position maintaining the same relative position on canvas after resize */
export function calculateNewRelativePosition(
    currentPosition: Position,
    objectDims: RotatedDimensions,
    originalCanvasSize: Size,
    newCanvasSize: Size
) {
    const currentCenter = {
        x: currentPosition.x + objectDims.width / 2 - objectDims.offsetX,
        y: currentPosition.y + objectDims.height / 2 - objectDims.offsetY,
    };

    // Calculate the percentage position (0-1) of the center
    const percentagePosition = {
        x: currentCenter.x / originalCanvasSize.width,
        y: currentCenter.y / originalCanvasSize.height,
    };

    // Calculate new center position maintaining the same percentage
    const newCenter = {
        x: percentagePosition.x * newCanvasSize.width,
        y: percentagePosition.y * newCanvasSize.height,
    };

    // Convert center position back to top-left position
    return {
        x: newCenter.x - objectDims.width / 2 + objectDims.offsetX,
        y: newCenter.y - objectDims.height / 2 + objectDims.offsetY,
    };
}

/**
 * Keeps all objects within bounds after canvas resize or object rotation
 * @param page - Page containing objects to adjust
 * @param canvasSize - New canvas dimensions
 *
 * Process:
 * 1. Loop through each object on the page
 * 2. Calculate its rotated dimensions
 * 3. Find safe position within new canvas bounds
 * 4. Update object position if needed
 */
export function adjustPageObjects(page: PageType, originalCanvasSize: Size, canvasSize: Size, skipAvatar: boolean = false): void {
    page.children.forEach(object => {
        if (skipAvatar && checkPolotnoType(object, polotnoTypes.Avatar)) {
            return;
        }

        const rotatedDims = calculateRotatedDimensions(object.width, object.height, object.rotation);
        const newRelativePosition = calculateNewRelativePosition({ x: object.x, y: object.y }, rotatedDims, originalCanvasSize, canvasSize);

        object.set(newRelativePosition);
    });
}

/**
 * Main function to handle canvas resizing
 * @param newSize - Desired canvas dimensions
 * @param store - Store containing all pages/objects
 *
 * Steps:
 * 1. Update store dimensions
 * 2. Update each page size
 * 3. Adjust all objects on each page to stay in bounds
 */
export function updateCanvasSize({ cropMode, store }: { cropMode: CropMode; store: StoreType }): void {
    const newSize = resolutions[cropMode];

    const originalCanvasSize = { width: store.width, height: store.height };

    store.setSize(newSize.width, newSize.height);
    store.pages.forEach(page => {
        // Fit canvas to avatar size
        const avatar = getAvatarElementFromPage(page);
        if (cropMode !== CropMode.Fit || !avatar) {
            page.setSize(newSize);
            adjustPageObjects(page, originalCanvasSize, newSize);
            return;
        }

        // skip if avatar has no original size
        if (!avatar.custom.originalWidth || !avatar.custom.originalHeight) {
            return;
        }

        // Handle deprecated fit crop mode
        const [width, height] = getBoundedAvatarSize({
            width: avatar.custom.originalWidth,
            height: avatar.custom.originalHeight,
        });

        // Set canvas size to selected crop
        page.set({ width, height });
        // Set avatar size and position
        avatar.set({ x: 0, y: 0, height, width });

        adjustPageObjects(page, originalCanvasSize, { width, height }, true);
    });
}
